import type {
  FxRateDocument,
  PortfolioReport,
} from '@interfaces/portfolioReport';
import { CurrencyCodes } from '@onevesthq/ov-enums';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { TableCell, TableRow } from '@ovComponents/2-component/table/table';
import { CurrencyFlag } from '@ovComponents/3-pattern/currencyFlag/currencyFlag';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Box, Grid, Tooltip, Typography, useTheme } from 'hephaestus/web';
import { useMemo } from 'react';
import {
  TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_ROW,
  TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_ROW_CELL,
} from '../portfolioTestIds';
import ChangeTooltip from './changeTooltip';
import type {
  DraftSubTradeRequest,
  PortfolioSecurity,
  PortfolioSubAccount,
} from './interfaces';
import SmallDollarPercentage from './smallDollarPercentage';
import TaxRank from './taxRank';
import {
  PORTFOLIO_CELL_HEIGHT,
  PORTFOLIO_COLUMN_WIDTH,
  PORTFOLIO_ROW_HEIGHT,
  sumPendingTradesCentsByCondition,
  sumSubAccountsCashOnHoldToTradeCents,
  sumSubAccountsCurrentCents,
} from './utils';

export const SecurityTableRow = ({
  isCash = false,
  security,
  subAccounts,
  portfolioReport,
  draftTrades,
  pendingTrades,
  isMakeHigher = false,
  index = 0,
  isMultiCurrencyPortfolio = false,
  fxRate = 1,
  currency = CurrencyCodes.CAD,
  fxRates = [],
  isAssetClassMode = false,
  assetGroupLabel,
  hideCurrentChangeTooltip = false,
  showPortfolioWideTargetPercentages = false,
  statusBadge,
  deviationHint,
  cashDisplayProposedEnd = false,
  asTableCells = false,
  auditProposedSummary = false,
  auditSummaryColumnBorder = true,
  auditOmitSummaryColumn = false,
  cashBreakdownLabel,
  cashBreakdownLines = [],
  cashBreakdownTooltip,
}: {
  isCash?: boolean;
  security: PortfolioSecurity;
  subAccounts: PortfolioSubAccount[];
  portfolioReport?: PortfolioReport;
  draftTrades: Map<string, DraftSubTradeRequest>;
  pendingTrades: Map<string, DraftSubTradeRequest>;
  isMakeHigher?: boolean;
  index?: number;
  isMultiCurrencyPortfolio?: boolean;
  fxRate?: number;
  currency?: CurrencyCodes;
  fxRates?: FxRateDocument[];
  isAssetClassMode?: boolean;
  assetGroupLabel?: string;
  hideCurrentChangeTooltip?: boolean;
  /** ASSET_CLASS only: when true, appends portfolio-wide % inline after within-class %. */
  showPortfolioWideTargetPercentages?: boolean;
  statusBadge?: { label: string; color: 'positive' | 'negative' };
  /** Optional hint when a no-trade dollar gap reflects whole-share granularity. */
  deviationHint?: string;
  /** Audit rebalance: cash current = post-trade target (matches securities end-state). */
  cashDisplayProposedEnd?: boolean;
  /** Renders summary cells only — parent owns the TableRow (audit unified table). */
  asTableCells?: boolean;
  /** Bulk rebalance audit: one proposed $/% column; omit live-widget current/deviation. */
  auditProposedSummary?: boolean;
  /** When false, Proposed column omits the divider (Change column owns it). */
  auditSummaryColumnBorder?: boolean;
  /** Parent renders Target / Proposed / Deviation columns (bulk rebalance audit grid). */
  auditOmitSummaryColumn?: boolean;
  /** Multi-currency audit: native CAD/USD cash pools (current). */
  cashBreakdownLabel?: string;
  cashBreakdownLines?: string[];
  cashBreakdownTooltip?: string;
}) => {
  const theme = useTheme();
  const showCashBreakdown =
    isCash && (cashBreakdownLines.length > 0 || Boolean(cashBreakdownLabel));
  const subAccountsCurrentCents = useMemo(
    () =>
      sumSubAccountsCurrentCents(
        subAccounts,
        [draftTrades, pendingTrades],
        currency,
        fxRate,
        fxRates,
      ),
    [subAccounts, draftTrades, pendingTrades, fxRate, currency, fxRates],
  );

  const subAccountsCashOnHoldToTradeCents = useMemo(
    () => sumSubAccountsCashOnHoldToTradeCents(subAccounts),
    [subAccounts],
  );

  const pendingTradesCents = useMemo(
    () =>
      sumPendingTradesCentsByCondition({
        maps: [draftTrades, pendingTrades],
        callbackFn: isCash
          ? undefined
          : (trade) => trade.financialProductId === security.id,
        currency,
        fxRates,
      }),
    [draftTrades, pendingTrades, security, isCash, currency, fxRates],
  );

  const { ticker, financialProductTaxRank, translatedName } = security;
  const productCurrentPriceCents = security.currentPriceCents * fxRate;

  const currentPriceCents = isCash ? 100 : productCurrentPriceCents;
  const expectedValueCents = isCash
    ? (portfolioReport?.expectedCashCents ?? 0) * fxRate -
      subAccountsCashOnHoldToTradeCents * fxRate
    : security.expectedValueCents * fxRate;
  const currentValueCents = isCash
    ? cashDisplayProposedEnd
      ? expectedValueCents
      : subAccountsCurrentCents
    : security.valueCents * fxRate + pendingTradesCents;
  const currentValueCentsOriginal = isCash
    ? (portfolioReport?.cashCents ?? 0) * fxRate
    : security.valueCents * fxRate;
  const currentValueCentsChange = isCash
    ? pendingTradesCents * -1
    : pendingTradesCents;
  const withinClassTargetPercentage = isCash
    ? portfolioReport?.cashAssetClassPercentage
    : security.assetClassPercentage;
  const hasWithinClassTargetPercentage =
    isAssetClassMode && withinClassTargetPercentage != null;
  const portfolioWideTargetPercentage = isCash
    ? portfolioReport?.expectedCashPercentage
    : security.expectedPercentage;
  const expectedPercentage =
    isAssetClassMode && hasWithinClassTargetPercentage
      ? withinClassTargetPercentage
      : isCash
        ? (portfolioReport?.expectedCashPercentage ?? 0)
        : security.expectedPercentage;
  const secondaryTargetPercentage =
    showPortfolioWideTargetPercentages &&
    hasWithinClassTargetPercentage &&
    portfolioWideTargetPercentage != null
      ? portfolioWideTargetPercentage
      : undefined;
  const deviationBase = expectedValueCents;
  const computedDeviationCents = isCash
    ? currentValueCents - expectedValueCents
    : security.valueCents * fxRate + pendingTradesCents - deviationBase;
  const deviationCents = computedDeviationCents;
  const roundedDeviationBase = Math.round(deviationBase);
  const deviationPercentage =
    roundedDeviationBase === 0
      ? 0
      : Math.round(computedDeviationCents) / roundedDeviationBase;
  const isCashRebalanceRequired =
    isCash &&
    (portfolioReport?.transferRebalanceRequired ||
      portfolioReport?.liquidateRebalanceRequired);

  const hasDeviation = Math.abs(deviationCents) > 0;
  const showDeviationHint = Boolean(deviationHint) && hasDeviation;

  const deviationDisplay = (
    <SmallDollarPercentage
      showColor
      cents={deviationCents}
      percentage={deviationPercentage}
      isRedColor={isCashRebalanceRequired}
    />
  );

  const rowHeight = isMakeHigher
    ? PORTFOLIO_ROW_HEIGHT.extended
    : PORTFOLIO_ROW_HEIGHT.standard;

  const cells = (
    <>
      {isAssetClassMode && (
        <TableCell sx={{ width: PORTFOLIO_COLUMN_WIDTH.expand, padding: 0 }} />
      )}

      {isAssetClassMode && (
        <TableCell
          sx={{ minWidth: PORTFOLIO_COLUMN_WIDTH.assetGroup }}
          data-testid={TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_ROW_CELL(
            index,
            'assetGroup',
          )}
        >
          {assetGroupLabel && (
            <Typography
              variant='bodySmall'
              component='p'
              sx={{ fontWeight: 'bold' }}
            >
              {assetGroupLabel}
            </Typography>
          )}
        </TableCell>
      )}

      <TableCell
        sx={{
          minWidth: PORTFOLIO_COLUMN_WIDTH.security,
          ...(isCash
            ? { height: PORTFOLIO_CELL_HEIGHT.cash }
            : {
                height: isMakeHigher
                  ? PORTFOLIO_CELL_HEIGHT.extended
                  : PORTFOLIO_CELL_HEIGHT.standard,
              }),
        }}
        data-testid={TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_ROW_CELL(
          index,
          'security',
        )}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {!!financialProductTaxRank && (
            <TaxRank taxRank={financialProductTaxRank} />
          )}

          {isMultiCurrencyPortfolio && !showCashBreakdown && (
            <Box>
              <CurrencyFlag
                height={20}
                width={20}
                baseCurrency={security.originalCurrency ?? CurrencyCodes.CAD}
              />
            </Box>
          )}

          <Typography variant='bodySmall' component='p'>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'start',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <Tooltip title={translatedName}>
                  <span>{ticker}</span>
                </Tooltip>
                {statusBadge && (
                  <Badge label={statusBadge.label} color={statusBadge.color} />
                )}
                {!isCash &&
                  security.settlementDays != null &&
                  security.settlementDays > 0 && (
                    <Badge
                      label={`T+${security.settlementDays}`}
                      color='neutral'
                      sx={{
                        fontSize: '10px',
                        lineHeight: 1,
                        padding: '0px 3px',
                      }}
                    />
                  )}
              </Box>

              {showCashBreakdown ? (
                <Tooltip
                  title={cashBreakdownTooltip ?? ''}
                  disableHoverListener={!cashBreakdownTooltip}
                >
                  <Box
                    component='span'
                    data-testid='portfolio-cash-breakdown-label'
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.125,
                      mt: 0.125,
                    }}
                  >
                    {(cashBreakdownLines.length > 0
                      ? cashBreakdownLines
                      : [cashBreakdownLabel!]
                    ).map((line) => (
                      <Typography
                        key={line}
                        variant='labelSmall'
                        color='textSecondary'
                        component='span'
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          lineHeight: 1.35,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Box>
                </Tooltip>
              ) : (
                formatMoneyValue(currentPriceCents)
              )}
            </Box>
          </Typography>
        </Box>
      </TableCell>

      {!(auditProposedSummary && auditOmitSummaryColumn) && (
        <TableCell
          right
          sx={{
            ...(auditProposedSummary && auditSummaryColumnBorder
              ? { borderRight: `1px solid ${theme.color.outlineVariant}` }
              : {}),
            minWidth: PORTFOLIO_COLUMN_WIDTH.security,
          }}
          data-testid={TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_ROW_CELL(
            index,
            'target',
          )}
        >
          <SmallDollarPercentage
            cents={expectedValueCents}
            percentage={expectedPercentage}
            secondaryPercentage={secondaryTargetPercentage}
            showPercentage={!auditProposedSummary}
          />
        </TableCell>
      )}

      {!auditProposedSummary && (
        <TableCell
          right
          sx={{ minWidth: PORTFOLIO_COLUMN_WIDTH.security }}
          data-testid={TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_ROW_CELL(
            index,
            'current',
          )}
        >
          <Grid
            container
            sx={{
              justifyContent: 'end',
              alignItems: 'center',
              flexWrap: 'nowrap',
            }}
          >
            <Grid>
              <SmallDollarPercentage
                cents={currentValueCents}
                showPercentage={false}
              />
            </Grid>

            {!hideCurrentChangeTooltip && (
              <ChangeTooltip
                change={currentValueCentsChange}
                original={currentValueCentsOriginal}
                flip={isCash}
                total
              />
            )}
          </Grid>
        </TableCell>
      )}

      {!auditProposedSummary && (
        <TableCell
          right
          sx={{
            borderRight: '1px solid grey',
            minWidth: PORTFOLIO_COLUMN_WIDTH.security,
          }}
          data-testid={TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_ROW_CELL(
            index,
            'deviation',
          )}
        >
          {showDeviationHint ? (
            <Tooltip
              title={
                <div style={{ whiteSpace: 'pre-line' }}>{deviationHint}</div>
              }
            >
              <Box
                component='span'
                sx={{
                  display: 'inline-block',
                  cursor: 'help',
                  borderBottom: '1px dotted',
                }}
              >
                {deviationDisplay}
              </Box>
            </Tooltip>
          ) : (
            deviationDisplay
          )}
        </TableCell>
      )}
    </>
  );

  if (asTableCells) {
    return cells;
  }

  return (
    <TableRow
      sx={{ height: rowHeight }}
      data-testid={TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_ROW(index)}
    >
      {cells}
    </TableRow>
  );
};
